/** «Toʻplamlar» tabi (demo `Sets`, `segT/q/topicSel`): darajalar, mavzular, kartochkalarim, qidiruv. */
import { create } from 'zustand';
import { W, TOPICS, LEVEL_ORDER, LEVEL_N } from '../data';
import { useStore, S, st, started } from '../store';
import { nav } from '../lib/nav';
import { startLesson } from './lesson/engine';
import { fire, sync } from '../lib/sync';
import { t, tn } from '../i18n';
import { Ic } from '../ui/Icon';
import { FM } from '../data/forms';
import { WordList } from '../ui/WordList';
import type { Word } from '../data/types';

export { WordList };

export type SetsSeg = 'levels' | 'topics' | 'mine';
interface SetsUi { segT: SetsSeg; q: string; topicSel: string | null; set: (p: Partial<Pick<SetsUi, 'segT' | 'q' | 'topicSel'>>) => void }
/** Tab ichki holati (demo `segT`, `q`, `topicSel`) — tab almashganda saqlanib qoladi. */
export const useSetsUi = create<SetsUi>()((set) => ({ segT: 'levels', q: '', topicSel: null, set: (p) => set(p) }));

const TINTS = ['var(--peach-soft)', 'var(--info-soft)', 'var(--success-soft)', 'var(--primary-soft)'];
/** Daraja kartasi: nom va nishon rangi. */
const LV: Record<string, [string, string]> = {
  A1: ['Boshlangʻich', 'success'], A2: ['Elementar', 'info'], B1: ['Oʻrta', 'primary'],
  B2: ['Oʻrtadan yuqori', 'peach'], C1: ['Ilgʻor', 'info'],
};
/** Ro'yxatlarda bir martada ko'rsatiladigan so'zlar (minglab qator DOM'ni sekinlashtiradi). */
const LIST_MAX = 200;
/** 1 143 → «1 143» (dizayndagi kabi bo'shliq bilan). */
const fmtN = (n: number) => n.toLocaleString('ru-RU').replace(/\s/g, ' ');
const SEGS: Array<[SetsSeg, string]> = [['levels', 'Darajalar'], ['topics', 'Mavzular'], ['mine', 'Kartochkalarim']];

export function Sets() {
  useStore((s) => s.w); // st()/started() uchun obuna
  const sets = useStore((s) => s.sets) ?? [];
  const mode = useStore((s) => s.mode);
  const activeId = useStore((s) => s.activeSet);
  const lvl = useStore((s) => s.lvl);
  const { segT, q, topicSel, set } = useSetsUi();

  const topics = TOPICS.map((tp) => ({ t: tp, n: W.filter((w) => w.topic === tp).length, l: W.filter((w) => w.topic === tp && st(w.id)).length }));
  const stn = started().length;
  const qq = q.toLowerCase().trim();

  let body: React.ReactNode;
  if (q.trim()) {
    body = <CappedList ws={W.filter((w) => w.w.toLowerCase().includes(qq) || w.uz.toLowerCase().includes(qq))} />;
  } else if (topicSel) {
    body = (
      <>
        <div className="between">
          <button className="row g6 t15b" style={{ color: 'var(--primary-ink)' }} onClick={() => set({ topicSel: null })}><Ic n="back" size={18} /> {t(topicSel)}</button>
        </div>
        <CappedList ws={W.filter((w) => w.topic === topicSel)} />
      </>
    );
  } else {
    let inner: React.ReactNode;
    if (segT === 'levels') {
      inner = (
        <>
          <div className="col g10">
            {LEVEL_ORDER.map((lv) => {
              const n = LEVEL_N[lv] ?? 0, [name, c] = LV[lv]!, on = lv === lvl;
              const badge = <span className="badge" style={{ background: `var(--${c}-soft)`, color: `var(--${c}-ink)` }}>{lv}</span>;
              if (!n) return <div key={lv} className="level"><span className="row g12">{badge}<span className="grow col g4"><b className="t16b">{t(name)}</b></span><span className="soon">{t('Tez orada')}</span></span></div>;
              const l = W.filter((w) => w.level === lv && st(w.id)).length;
              const pick = () => { if (!on) { S().set({ lvl: lv }); fire(sync.course({ level: lv }), 'level'); } startLesson(); };
              return (
                <button key={lv} className={`level ${on ? 'on' : ''}`} onClick={pick}>
                  <span className="row g12">
                    {badge}
                    <span className="grow col g4"><b className="t16b">{t(name)}</b></span>
                    <span className="soon" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}>{on ? (l ? t('Davom etish') : t('Boshlash')) : t('Tanlash')}</span>
                  </span>
                  {(on || l > 0) && <span className="row g10"><span className="track"><i style={{ width: `${Math.round(l / n * 100)}%` }} /></span><span className="t13" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{tn('# ta boshlangan', fmtN(l))}</span></span>}
                </button>
              );
            })}
          </div>
          <button className="qa2" onClick={() => nav.push({ k: 'forms', from: 'sets' })}>
            <span className="sq2" style={{ background: 'var(--success-soft)', color: 'var(--success-ink)' }}><Ic n="layers" /></span>
            <span className="grow col g4" style={{ textAlign: 'left' }}><b className="t15b">{t('Soʻz shakllari')} · {t(FM.title)}</b><span className="t13">{t(FM.sub)}</span></span>
            <span className="chev2"><Ic n="chevRight" size={16} /></span>
          </button>
          <div className="col g10">
            <div className="between"><b className="t16b">{t('Mashhur mavzular')}</b><button className="t13" style={{ color: 'var(--primary-ink)', fontWeight: 700 }} onClick={() => set({ segT: 'topics' })}>{t('Barchasi')}</button></div>
            <div className="topics">
              {topics.slice().sort((a, b) => b.n - a.n).slice(0, 3).map((x, i) => (
                <button key={x.t} className="topic" style={{ background: TINTS[i] }} onClick={() => set({ topicSel: x.t })}><b>{t(x.t)}</b>{x.l > 0 && <span>{tn('# ta boshlangan', x.l)}</span>}</button>
              ))}
            </div>
          </div>
        </>
      );
    } else if (segT === 'topics') {
      inner = (
        <div className="topics" style={{ gridTemplateColumns: '1fr 1fr' }}>
          {topics.map((x, i) => (
            <button key={x.t} className="topic" style={{ background: TINTS[i % 4] }} onClick={() => set({ topicSel: x.t })}><b>{t(x.t)}</b>{x.l > 0 && <span>{tn('# ta boshlangan', x.l)}</span>}</button>
          ))}
        </div>
      );
    } else {
      inner = (
        <>
          <button className="btn" style={{ gap: 8 }} onClick={() => nav.push({ k: 'setEditor', id: null })}><Ic n="plus" />{t('Yangi kartochka')}</button>
          <p className="t12" style={{ margin: 0 }}>{t('Bitta kartochkaga 30 tagacha soʻz. Oʻquv markazidagi har dars uchun alohida kartochka oching.' + (mode !== 'center' ? ' (Kartochka faollashtirilsa, darslar shu kartochkadan boradi.)' : ''))}</p>
          {sets.length ? (
            <div className="col g10">
              {sets.slice().reverse().map((s) => {
                const n = s.ids.length, l = s.ids.filter((id) => st(id)).length, on = s.id === activeId && mode === 'center';
                return (
                  <div key={s.id} className={`setrow ${on ? 'on' : ''}`}>
                    <div className="grow col g4" style={{ minWidth: 0 }}>
                      <span className="row g8"><b className="t15b">{s.name}</b>{on && <span className="soon" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)', padding: '2px 8px' }}>{t('Joriy')}</span>}</span>
                      <span className="row g8"><span className="track"><i style={{ width: `${n ? Math.round(l / n * 100) : 0}%` }} /></span><span className="t12" style={{ fontWeight: 700 }}>{tn('#/# oʻrganildi', l, n)}</span></span>
                    </div>
                    <button className="nav" aria-label={t('Tahrirlash')} onClick={() => nav.push({ k: 'setEditor', id: s.id })}><Ic n="edit" size={18} /></button>
                    <button className="btn" style={{ width: 'auto', height: 40, padding: '0 14px', fontSize: 14 }} onClick={() => { S().set({ mode: 'center', activeSet: s.id }); fire(sync.me({ mode: 'center' }).then(() => sync.course({ active_set_id: s.id })), 'active-set'); startLesson(); }}>{t('Oʻrganish')}</button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card col g8" style={{ padding: 20, alignItems: 'center', textAlign: 'center' }}>
              <span className="ibox" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}><Ic n="layers" /></span>
              <b className="t15b">{t('Hali kartochka yoʻq')}</b>
              <span className="t13">{t('“Yangi kartochka” tugmasini bosing va darsdagi soʻzlarni bazadan tanlab qoʻshing.')}</span>
            </div>
          )}
          {stn > 0 && <div className="col g8"><b className="t15b">{tn('Barcha oʻrganilgan soʻzlar · #', stn)}</b><WordList ws={started()} /></div>}
        </>
      );
    }
    body = (
      <>
        <div className="seg" role="group">
          {SEGS.map(([k, l]) => <button key={k} aria-pressed={segT === k} onClick={() => set({ segT: k })}>{t(l)}</button>)}
        </div>
        {inner}
      </>
    );
  }

  return (
    <>
      <h1 className="h1">{t('Toʻplamlar')}</h1>
      <label className="search"><Ic n="search" size={20} /><input type="search" placeholder={t('Lugʻatdan soʻz qidirish')} value={q} onChange={(e) => set({ q: e.target.value })} /></label>
      <div className="col" style={{ gap: 18 }}>{body}</div>
    </>
  );
}

/** Uzun ro'yxat: birinchi LIST_MAX tasi + qolganini qidiruv bilan toraytirish haqida izoh. */
function CappedList({ ws }: { ws: Word[] }) {
  return (
    <>
      <WordList ws={ws.slice(0, LIST_MAX)} />
      {ws.length > LIST_MAX && <p className="t12" style={{ margin: 0 }}>{tn('Yana # ta soʻz — qidiruv bilan toraytiring', ws.length - LIST_MAX)}</p>}
    </>
  );
}
